import { ChangeType } from "../interfaces/DTO/agent/mimshakGroupBy";
import { InsuranceType, PayoutMethod } from "../interfaces/DTO/mislaka_xml_insurence_models";
import { AdjustedProductType, ContributionType, PolicyStatus, ProductType } from "../interfaces/DTO/mislaka_xml_models";
import { PolicyEmploymentStatus, RelationType, SurvivorsWaiverType } from "../interfaces/DTO/mislaka_xml_pension_models";
import { UserTaskOrigin, UserTaskStatus, UserTaskSystemStatus, UserTaskType } from "../interfaces/DTO/userTask/UserTask";
import { ManualProductType } from "../interfaces/DTO/manualMimshakData";
import { BankHandlerCode } from "./bankHandlerCodeFormatter";
import { InvestmentHouseCode } from "./investmentHouseCodeFormatter";
import { FormTemplateStatus } from "../interfaces/DTO/forms/formTemplate";
import { isInvestmentPortfolioSubType } from "./productTypeUniversalFormatter";

export const colorProductType = (type: ProductType): string => {
  switch (type) {
    case ProductType.LIFE_INSURANCE_SAVINGS:
      return "#612ea3"; // פוליסת ביטוח חיים משולב חיסכון
    case ProductType.PENSION_FUND:
      return "#9F5F95"; // קרן פנסיה
    case ProductType.PROVIDENT_FUND:
      return "#88B04B"; // קופת גמל
    case ProductType.EDUCATION_FUND:
      return "#F7CAC9"; // קרן השתלמות
    case ProductType.PURE_SAVINGS_POLICY:
      return "#92A8D1"; // פוליסת חיסכון טהור
    case ProductType.PURE_RISK_POLICY:
      return "#FF7F50"; // פוליסת סיכון טהור
    case ProductType.MORTGAGE_LIFE_INSURANCE:
      return "#E549F0"; // ביטוח חיים משכנתא
    case ProductType.COLLECTIVE_RISK_POLICY:
      return "#45B0AC"; // פוליסת סיכון טהור קולקטיב
    case ProductType.INVESTMENT_PROVIDENT_FUND:
      return "#955251"; // קופת גמל להשקעה
    case ProductType.CHILD_SAVINGS:
      return "#B565A7"; // חיסכון לכל ילד
    default:
      return "#ffffff"; // ברירת מחדל - לבן (סוג לא ידוע)
  }
};
let cachedAdjustedColorMap: Record<AdjustedProductType, string> | null = null;

export const colorAdjustedProductType = (type: AdjustedProductType): string => {
  if (!cachedAdjustedColorMap) {
    cachedAdjustedColorMap = {
      [AdjustedProductType.LIFE_INSURANCE_SAVINGS]: "#612ea3",
      [AdjustedProductType.PENSION_FUND]: "#9F5F95",
      [AdjustedProductType.PROVIDENT_FUND]: "#88B04B",
      [AdjustedProductType.EDUCATION_FUND]: "#F7CAC9",
      [AdjustedProductType.PURE_SAVINGS_POLICY]: "#92A8D1",
      [AdjustedProductType.PURE_RISK_POLICY]: "#FF7F50",
      [AdjustedProductType.MORTGAGE_LIFE_INSURANCE]: "#E549F0",
      [AdjustedProductType.COLLECTIVE_RISK_POLICY]: "#45B0AC",
      [AdjustedProductType.INVESTMENT_PROVIDENT_FUND]: "#955251",
      [AdjustedProductType.CHILD_SAVINGS]: "#B565A7",

      // חדשים – משויכים לצבע לפי הקטגוריה הראשית:
      [AdjustedProductType.ACTIVE_MANAGERS_INSURANCE]: "#612ea3", // כמו LIFE_INSURANCE_SAVINGS
      [AdjustedProductType.INACTIVE_MANAGERS_INSURANCE]: "#612ea3",

      [AdjustedProductType.MANDATORY_PENSION_FUND]: "#9F5F95", // כמו PENSION_FUND
      [AdjustedProductType.SUPPLEMENTARY_PENSION_FUND]: "#9F5F95",
      [AdjustedProductType.BASIC_PENSION_FUND]: "#9F5F95",
      [AdjustedProductType.OLD_COMPREHENSIVE_PENSION]: "#7B4F9D", // סגול כהה יותר - פנסיה מקיפה ותיקה
      [AdjustedProductType.OLD_BASIC_PENSION]: "#6A3D8C", // סגול עמוק - פנסיה יסוד ותיקה

      [AdjustedProductType.CAPITAL_PROVIDENT_FUND]: "#88B04B", // כמו PROVIDENT_FUND
      [AdjustedProductType.PENSION_PROVIDENT_FUND]: "#88B04B",
      [AdjustedProductType.COMBINED_PROVIDENT_FUND]: "#88B04B",
    };
  }

  return cachedAdjustedColorMap[type] ?? "#ffffff";
};
export const colorByFundId = (id: number): string => {
  const hash = id;
  const r = 70 + (hash * 13) % 180;
  const g = 80 + (hash * 17) % 160;
  const b = 90 + (hash * 23) % 140;
  return `rgba(${r}, ${g}, ${b}, 0.9)`;
};
export const colorFund=(fundId: number, baseColor: string): string=> {
  const rBase = parseInt(baseColor.slice(1, 3), 16);
  const gBase = parseInt(baseColor.slice(3, 5), 16);
  const bBase = parseInt(baseColor.slice(5, 7), 16);
  const alpha = 0.33 + (fundId % 10) * 0.066; // ערכים בין 0.33 ל־0.99 // ערכים בין 0.2 ל־0.99
  return `rgba(${rBase}, ${gBase}, ${bBase}, ${alpha.toFixed(2)})`;
}
const handlerColors: Record<string, string> = {
  "511880460": "#9a8b9fff", // אנליסט - blue gray 200
  "513611509": "#b39ddb", // ילין לפידות - indigo 200
  "513173393": "#7b8dedff", // אלטשולר - indigo 200
  "520023185": "#366375ff", // פניקס - indigo 100
  "513026484": "#d1c4e9", // פניקס - light lilac
  "512237744": "#5a7480ff", // מגדל - blue gray 100
  "512267592": "#bbdefb", // הראל - light blue 100
  "520004078": "#b2dfdb", // הראל ביטוח - teal 100
  "512065202": "#44575aff", // מיטב - custom soft blue-gray
  "512245812": "#d7ccc8", // מנורה - warm gray
  "513621110": "#7dacceff", // אינפיניטי - soft cool gray
  "514956465": "#c89393ff", // מור - neutral light gray
  "520042177": "#e0e0e0", // הכשרה ביטוח - pale gray
  "512244146": "#90a4ae", // כלל - blue gray 300 (כהה מהאחרים)
  "520024647": "#5e785fff", // כלל ביטוח - blue gray 200
  "520042540": "#d3d3d3", // מנורה מבטחים - light silver gray
  "520004896": "#667a83ff", // מגדל ביטוח - classic light gray
  "512304882": "#626ca7ff", // AIG - soft indigo
};

export const colorHandlerCode = (handlerCode: string | number): string => {
  if (!handlerCode) return "#cccccc"; // צבע אפור בהיר כברירת מחדל
  const code = handlerCode.toString();
  if (!code) return "#cccccc";

  // אם יש צבע מוגדר מראש
  if (handlerColors[code]) return handlerColors[code];

  // אם לא – גזירת צבע מ-HSL על בסיס הקוד
  const numericCode = Number(code.replace(/\D/g, ""));
  const hue = numericCode % 240; // הגבלת תחום הצבעים לאזור קריר (כחול-סגול)
  const saturation = 30 + (numericCode % 30); // בין 30% ל-60% רכות
  const lightness = 50 + (numericCode % 10); // בין 50% ל-60% בהירות

  return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
};
export const colorFormTemplateStatus = (status: FormTemplateStatus): string => {
  switch (status) {
    case FormTemplateStatus.DRAFT:
      return "#f57c00"; // כתום
    case FormTemplateStatus.PUBLISHED:
      return "#388e3c"; // ירוק
    case FormTemplateStatus.ARCHIVED:
      return "#9e9e9e"; // אפור
    default:
      return "#9e9e9e"; // אפור כברירת מחדל
  }
};
export const colorPayoutMethod = (method: PayoutMethod): string => {
    switch (method) {
      case PayoutMethod.LUMP_SUM:
        return "#1976d2"; // כחול
      case PayoutMethod.PENSION:
        return "#388e3c"; // ירוק
      case PayoutMethod.PERIODIC_PAYMENTS:
        return "#fbc02d"; // צהוב
      case PayoutMethod.OTHER:
        return "#9e9e9e"; // אפור
      case PayoutMethod.CAPITAL_AND_PENSION:
        return "#7b1fa2"; // סגול
      default:
        return "#9e9e9e"; // אפור כברירת מחדל
    }
  };
export const colorRelationType = (relationType: RelationType): string => {
  switch (relationType) {
    case RelationType.PARENTS:
      return "#ff7f50";
    case RelationType.SPOUSE:
      return "#6495ed";
    case RelationType.CHILD:
      return "#32cd32";
    case RelationType.SIBLING:
      return "#ff69b4";
    case RelationType.GIFT_GIVER:
      return "#8a2be2";
    case RelationType.GUARDIAN:
      return "#d2691e";
    case RelationType.EMPLOYER:
      return "#4682b4";
    case RelationType.OTHER:
      return "#808080";
    case RelationType.BANK_LENDER:
      return "#b22222";
    case RelationType.INSTITUTIONAL_LENDER:
      return "#008080";
    case RelationType.OTHER_LENDER:
      return "#ffa500";
    default:
      return "#808080"; // ברירת מחדל: OTHER
  }
};
// ✅ Re-export from insightsRuleUtils for backwards compatibility
export { colorRuleType } from "./insightsRuleUtils";

  export const colorInsuranceType = (type: InsuranceType): string => {
    switch (type) {
      case InsuranceType.DEATH:
        return "#d32f2f"; // אדום כהה
      case InsuranceType.OCCUPATIONAL_DISABILITY:
        return "#ff9800"; // כתום
      case InsuranceType.ACCIDENTAL_DEATH:
        return "#c2185b"; // ורוד כהה
      case InsuranceType.ACCIDENTAL_DISABILITY:
        return "#f57c00"; // כתום כהה
      case InsuranceType.LOSS_OF_WORK_CAPACITY:
        return "#512da8"; // סגול כהה
      case InsuranceType.PREMIUM_WAIVER:
        return "#00796b"; // ירוק כהה
      case InsuranceType.CRITICAL_ILLNESS:
        return "#c62828"; // אדום
      case InsuranceType.COMBINED_SAVINGS_PLAN:
        return "#2e7d32"; // ירוק
      case InsuranceType.DEATH_AND_DISABILITY:
        return "#303f9f"; // כחול כהה
      case InsuranceType.OTHER:
        return "#757575"; // אפור כהה
      default:
        return "#9e9e9e"; // אפור כברירת מחדל
    }
  };
  export const colorContributionType = (type: ContributionType): string => {
    const contributionColors: Record<number, string> = {
      1: "#2c3e50", // פיצויים
      2: "#8e44ad", // תגמולים עובד
      3: "#27ae60", // תגמולים מעביד
      4: "#d35400", // תגמולים47
      5: "#3498db", // אבדן כושר עבודה
      6: "#f39c12", // שונות עובד
      7: "#e74c3c", // שונות מעביד
      8: "#9b59b6", // קה"ש עובד
      9: "#16a085", // קה"ש מעביד
      10: "#34495e", // תגמולים פרט
      11: "#c0392b", // ריסק מוות (ללא חסכון)
      12: "#f1c40f", // חיסכון לכל ילד
    };
    return contributionColors[type] || "#7f8c8d"; // צבע ברירת מחדל
  };

  const policyStatusColors: Record<PolicyStatus, string> = {
  1: "#4CAF50",  // פעיל (ACTIVE)
  2: "#FF9800",  // מוקפא/מסולק/לא פעיל (NOT_ACTIVE)
  3: "#F44336",  // מבוטל (CANCELED)
  4: "#9E9E9E",  // ריסק זמני (TEMPORARY_RISK)
  5: "#3F51B5",  // בתביעה (CLAIM)
  6: "#795548",  // חסום להפקדות (DEPOSIT_BLOCKED)
  7: "#607D8B",  // תום תקופה (PERIOD_ENDED)
  8: "#00BCD4",  // ריסק זמני אוטומטי (AUTOMATIC_TEMPORARY_RISK)
  9: "#FFC107",  // שמירת כיסוי ביטוחי (INSURANCE_COVERAGE_MAINTAINED)
  10: "#999", // לא רלוונטי (NOT_RELEVANT)
};

const defaultColor = "#9E9E9E";

export const colorPolicyStatus = (status: PolicyStatus): string => {
  return policyStatusColors[status] || defaultColor;
};

  export const colorChangeType = (changeType: ChangeType): string => {
    switch (changeType) {
      case ChangeType.NEW_POLICY:
        return '#4CAF50'; // ✅ ירוק – הצטרפות חדשה
      case ChangeType.DEPOSIT_FEE_CHANGED:
        return '#FFC107'; // ✅ צהוב – שינוי בעמלת הפקדה
      case ChangeType.SAVING_FEE_CHANGED:
        return '#FFC107'; // ✅ צהוב – שינוי בעמלת חיסכון
      case ChangeType.DEPOSIT_AMOUNT_DECREASED:
        return '#9b621c'; // ✅ אדום – ירידה בסכום הפקדות
      case ChangeType.WITHDRAWAL_AMOUNT_INCREASED:
        return '#4CAF50'; // ✅ ירוק – עלייה בסכום משיכות
      case ChangeType.POLICY_BALANCE_ZEROED:
        return '#555555'; // ✅ אפור כהה – יתרה שהתאפסה
      case ChangeType.TRANSFER_OUT_CHANGED:
        return '#2196F3'; // ✅ כחול – שינוי בכספים יוצאים
      case ChangeType.TRANSFER_IN_CHANGED:
        return '#9C97B0'; // ✅ סגול – שינוי בכספים נכנסים
      case ChangeType.AGENT_APPOINTMENT_DATE_CHANGED:
       return '#9C27B0'; // ✅ סגול – שינוי בתאריך מינוי הסוכן
      case ChangeType.FUND_IDS_CHANGED:
        return '#d222fe'; // ✅ כתום – שינוי במזהי הקרנות
      case ChangeType.MISSING_POLICY:
       return '#9C20B0'; // ✅ סגול – שינוי בתאריך מינוי הסוכן
      default:
        return '#9E9E9E'; // ✅ אפור – מצב ברירת מחדל
    }
  };
  export function colorUserTaskStatus(status: UserTaskStatus | null | undefined): string {
  switch (status) {
    case UserTaskStatus.PENDING:
      return "#ff9800"; // כתום
    case UserTaskStatus.IN_PROGRESS:
      return "#2196f3"; // כחול
    case UserTaskStatus.COMPLETED:
      return "#4caf50"; // ירוק
    case UserTaskStatus.FAILED:
      return "#f4F036"; // אדום
  
    case UserTaskStatus.REJECTED:
      return "#f44336"; // אדום
    default:
      return "#9e9e9e"; // אפור (default)
  }
}
  export function colorUserTaskSystemStatus(status: UserTaskSystemStatus | null | undefined): string {
    switch (status) {
      case UserTaskSystemStatus.UNCHECKED:
        return "#9e9e9e"; // אפור - טרם נבדק
      case UserTaskSystemStatus.PENDING:
      case UserTaskSystemStatus.AWAITING:
        return "#ff9800"; // כתום - ממתין
      case UserTaskSystemStatus.MATCH:
        return "#4caf50"; // ירוק - תואם
      case UserTaskSystemStatus.MISMATCH:
      case UserTaskSystemStatus.FAILED:
        return "#f44336"; // אדום - אי-התאמה / שגיאה
      default:
        return "#9e9e9e";
    }
  }
  export function colorUserTaskOrigin(origin?: UserTaskOrigin): string {
    switch (origin) {
      case UserTaskOrigin.SIMULATION:
        return "#1976d2"; // כחול - סימולציה
      case UserTaskOrigin.MANUAL:
        return "#9c27b0"; // סגול - ידני
      case UserTaskOrigin.SYSTEM:
        return "#ff9800"; // כתום - מערכת
      case UserTaskOrigin.EXTERNAL:
        return "#009688"; // טורקיז - חיצוני
      default:
        return "#9e9e9e"; // אפור - לא ידוע
    }
  }
  export function colorUserTaskType(taskType: UserTaskType): string {
    switch (taskType) {
      case UserTaskType.TRANSFER_IN:
        return "#4caf50"; // ירוק - העברה פנימה
      case UserTaskType.TRANSFER_OUT:
        return "#f44386"; // אדום - העברה החוצה
      case UserTaskType.FEE_CHANGE:
        return "#ff9800"; // כתום - שינוי עמלות
      case UserTaskType.NEW_CLIENT:
        return "#2196f3"; // כחול - לקוח חדש
      case UserTaskType.JOIN_POLICY:
        return "#3f51b5"; // כחול כהה - הצטרפות
      case UserTaskType.OTHER:
        return "#9e9e9e"; // אפור - אחר
      case UserTaskType.JOIN_REJECT:
        return "#e91e63"; // ורוד כהה - ריג'קט
      case UserTaskType.DEPOSIT_TRACKING:
        return "#8bc34a"; // ירוק בהיר - הפקדות
      case UserTaskType.WITHDRAWAL:
        return "#ff8722"; // כתום כהה - משיכות
      
      case UserTaskType.LOAN_TRACKING:
        return "#795548"; // חום - הלוואות
      case UserTaskType.CONSULTING:
        return "#673ab7"; // סגול - ייעוץ
      case UserTaskType.POWER_OF_ATTORNEY:
        return "#009688"; // טורקיז - ייפוי כוח
      case UserTaskType.AGENT_UPDATE:
        return "#607d8b"; // כחול-אפור - עדכון סוכן
      case UserTaskType.LOST_ACCOUNT_TRACKING:
        return "#ffeb3b"; // צהוב - חשבונות מנותקי קשר
      case UserTaskType.AGENT_APPOINTMENT:
        return "#4db6ac"; // טורקיז בהיר - מינוי
      case UserTaskType.AGENT_CANCELLATION:
        return "#b71c1c"; // אדום כהה - ביטול מינוי
      case UserTaskType.PATH_CHANGE:
          return "#ba141c"; // אדום כהה - ביטול מינוי
      case UserTaskType.BENEFICIARY_UPDATE:
        return "#8d6e63"; // חום - עדכון מוטבים
      case UserTaskType.VIEW_POWER_OF_ATTORNEY:
        return "#5c6bc0"; // אינדיגו - ייפוי כוח לצפייה
      default:
        return "#000000"; // שחור כברירת מחדל
    }
  }

export const colorPolicyEmploymentStatus = (status: PolicyEmploymentStatus): string => {
  switch (status) {
    case PolicyEmploymentStatus.EMPLOYEE:
      return "#0288d1"; // כחול/תכלת - שכיר
    case PolicyEmploymentStatus.SELF_EMPLOYED:
      return "#9c27b0"; // סגול לילך - עצמאי
    case PolicyEmploymentStatus.CONTROLLING_OWNER:
      return "#f57c00"; // כתום - בעל שליטה
    case PolicyEmploymentStatus.OTHER:
      return "#757575"; // אפור - אחר
    case PolicyEmploymentStatus.PRIVATE:
      return "#00897b"; // טורקיז - פרטי
    case PolicyEmploymentStatus.SELF_EMPLOYED_AND_EMPLOYEE:
      return "#7b1fa2"; // סגול כהה - שכיר ועצמאי
    case PolicyEmploymentStatus.REIRED:
      return "#849c4cff"; // ירוק - פנסיונר
    default:
      return "#9e9e9e"; // אפור כברירת מחדל
  }
};

/**
 * פונקציית צבע לסוג מוצר ידני (ManualProductType)
 */
export const colorManualProductType = (type: ManualProductType): string => {
  switch (type) {
    case ManualProductType.PURE_SAVINGS_POLICY:
      return "#92A8D1"; // פוליסת חיסכון טהור - כמו ProductType רגיל
    case ManualProductType.REAL_ESTATE:
      return "#D4A574"; // נדל"ן - חום בהיר
    case ManualProductType.BANK_ACCOUNT:
      return "#4CAF50"; // עו"ש בנק - ירוק
    case ManualProductType.BANK_ACCOUNT_FOREIGN:
      return "#26A69A"; // עו"ש מט"ח - ירוק-ציאן
    case ManualProductType.DEPOSIT:
      return "#66BB6A"; // פקדונות - ירוק בהיר
    case ManualProductType.EMPLOYEE_COMPENSATION:
      return "#FF9800"; // פיצויים - כתום
    case ManualProductType.ALTERNATIVE_INVESTMENTS:
      return "#9C27B0"; // השקעות אלטרנטיביות - סגול
    case ManualProductType.INVESTMENT_PORTFOLIO:
      return "#E91E63"; // תיק השקעות - ורוד
    case ManualProductType.LOAN:
      return "#F44336"; // הלוואה - אדום
    case ManualProductType.MORTGAGE:
      return "#EF5350"; // משכנתא - אדום בהיר
    case ManualProductType.DISPOSABLE_INCOME:
      return "#42A5F5"; // הכנסה פנויה חודשית - כחול בהיר
    case ManualProductType.DISPOSABLE_INCOME_ANNUAL:
      return "#1E88E5"; // הכנסה פנויה שנתית - כחול כהה יותר
    case ManualProductType.OTHER:
      return "#607D8B"; // אחר - אפור כחלחל
    default:
      return "#9e9e9e"; // ברירת מחדל - אפור
  }
};

/**
 * פונקציית צבע אוניברסלית לסוג מוצר
 * מנסה ProductType רגיל, אחר כך ManualProductType, אחר כך ברירת מחדל
 */
export const colorProductTypeUniversal = (productType: number): string => {
  // ניסיון 1: AdjustedProductType (כולל ביטוח מנהלים 111/112)
  if (Object.values(AdjustedProductType).includes(productType as AdjustedProductType)) {
    return colorAdjustedProductType(productType as AdjustedProductType);
  }

  // ניסיון 2: ProductType רגיל (מהמסלקה)
  if (Object.values(ProductType).includes(productType as ProductType)) {
    return colorProductType(productType as ProductType);
  }

  // ניסיון 3: ManualProductType (נכסים ידניים)
  if (Object.values(ManualProductType).includes(productType as ManualProductType)) {
    return colorManualProductType(productType as ManualProductType);
  }

  // ניסיון 4: תת-סוג של תיק השקעות (6001 / 6002) - בצבע של תיק השקעות
  if (isInvestmentPortfolioSubType(productType)) {
    return colorManualProductType(ManualProductType.INVESTMENT_PORTFOLIO);
  }

  // ברירת מחדל
  return "#9e9e9e";
};

/**
 * מפת צבעים לבנקים לפי BankHandlerCode
 */
const bankColors: Record<string, string> = {
  [BankHandlerCode.LEUMI]: "#ED1C24",           // בנק לאומי - אדום
  [BankHandlerCode.DISCOUNT]: "#0066B3",        // בנק דיסקונט - כחול כהה
  [BankHandlerCode.HAPOALIM]: "#E4002B",        // בנק הפועלים - אדום
  [BankHandlerCode.IGUD]: "#1E5B8C",            // בנק אגוד - כחול
  [BankHandlerCode.OTSAR_HAHAYAL]: "#006B3D",   // בנק אוצר החייל - ירוק
  [BankHandlerCode.MERCANTILE]: "#004B87",      // בנק מרכנתיל - כחול כהה
  [BankHandlerCode.DIGITAL_FIRST]: "#00A8E1",   // הבנק הדיגיטלי הראשון - ציאן
  [BankHandlerCode.MIZRAHI_TEFAHOT]: "#FF6B35", // בנק מזרחי טפחות - כתום
  [BankHandlerCode.CITIBANK]: "#003DA5",        // סיטיבנק - כחול
  [BankHandlerCode.HSBC]: "#DB0011",            // HSBC - אדום
  [BankHandlerCode.BNP_PARIBAS]: "#00965E",     // BNP Paribas - ירוק
  [BankHandlerCode.U_BANK]: "#7B68EE",          // יובנק - סגול בהיר
  [BankHandlerCode.BARCLAYS]: "#00AEEF",        // ברקליס - תכלת
  [BankHandlerCode.TRADE]: "#1C4587",           // בנק למסחר - כחול כהה
  [BankHandlerCode.FIBI]: "#0055A4",            // הבנק הבינלאומי - כחול
  [BankHandlerCode.ARAB_ISRAELI]: "#228B22",    // בנק ערבי ישראלי - ירוק
  [BankHandlerCode.SBI]: "#134A8E",             // בנק SBI - כחול כהה
  [BankHandlerCode.MASSAD]: "#B8860B",          // בנק מסד - זהב כהה
  [BankHandlerCode.JERUSALEM]: "#8B4513",       // בנק ירושלים - חום
  [BankHandlerCode.POST]: "#FFD700",            // בנק הדואר - זהב
  [BankHandlerCode.YAHAV]: "#4169E1",           // בנק יהב - כחול מלכותי
};

/**
 * מפת צבעים לבתי השקעות לפי InvestmentHouseCode
 */
const investmentHouseColors: Record<string, string> = {
  [InvestmentHouseCode.INTERACTIVE_ISRAEL]: "#0066CC",   // אינטראקטיב ישראל - כחול
  [InvestmentHouseCode.MEITAV_DASH]: "#E4002B",          // מיטב דש - אדום
  [InvestmentHouseCode.ALTSHULER_SHAHAM]: "#00A651",     // אלטשולר שחם - ירוק
  [InvestmentHouseCode.YELIN_LAPIDOT]: "#FF6B00",        // ילין לפידות - כתום
  [InvestmentHouseCode.MOR]: "#9C27B0",                  // מור - סגול
  [InvestmentHouseCode.PHOENIX]: "#FF4500",              // הפניקס - כתום-אדום
  [InvestmentHouseCode.HAREL]: "#003DA5",                // הראל - כחול כהה
  [InvestmentHouseCode.MIGDAL]: "#1E3A8A",               // מגדל - כחול נייבי
  [InvestmentHouseCode.IBI]: "#DC143C",                  // איי.בי.אי. - אדום כהה
  [InvestmentHouseCode.ANALYST]: "#4A90E2",              // אנליסט - כחול בהיר
  [InvestmentHouseCode.SIGMA]: "#7B68EE",                // סיגמא - סגול בהיר
  [InvestmentHouseCode.EXCELLENCE]: "#FFD700",           // אקסלנס - זהב
  [InvestmentHouseCode.TAMIR_FISHMAN]: "#008B8B",        // תמיר פישמן - ציאן כהה
  [InvestmentHouseCode.INFINITY]: "#8B4513",             // אינפיניטי - חום
  [InvestmentHouseCode.PSAGOT]: "#2E8B57",               // פסגות - ירוק ים
};

/**
 * פונקציית צבע אוניברסלית לקוד יצרן (handler_code)
 * מנסה בנקים, בתי השקעות, חברות ביטוח, ואז ברירת מחדל
 */
export const colorHandlerCodeUniversal = (handlerCode: string): string => {
  if (!handlerCode) return "#cccccc";

  // ניסיון 1: בנקים
  if (bankColors[handlerCode]) {
    return bankColors[handlerCode];
  }

  // ניסיון 2: בתי השקעות
  if (investmentHouseColors[handlerCode]) {
    return investmentHouseColors[handlerCode];
  }

  // ניסיון 3: חברות ביטוח/קופות גמל (קיימות)
  if (handlerColors[handlerCode]) {
    return handlerColors[handlerCode];
  }

  // ברירת מחדל - צבע דינמי מה-HSL
  return colorHandlerCode(handlerCode);
};

/**
 * פונקציית צבע לסוג ויתור שארים (SurvivorsWaiverType)
 */
export const colorSurvivorsWaiverType = (value?: number | SurvivorsWaiverType): string => {
  switch (value) {
    case SurvivorsWaiverType.FULL_WAIVER: // ויתור מלא
      return "#d32f2f"; // אדום כהה
    case SurvivorsWaiverType.CHILDREN_ONLY: // ילדים בלבד
      return "#ff9800"; // כתום
    case SurvivorsWaiverType.SPOUSE_ONLY: // בן זוג בלבד
      return "#f57c00"; // כתום כהה
    default:
      return "#9e9e9e"; // אפור
  }
};

/**
 * פונקציית צבע לויתור כיסוי ביטוחי (InsuranceWaiver)
 */
export const colorInsuranceWaiver = (value?: number): string => {
  switch (value) {
    case 1: // יש
      return "#d32f2f"; // אדום כהה
    case 2: // אין
      return "#4caf50"; // ירוק
    case 3: // לא רלוונטי
      return "#9e9e9e"; // אפור
    default:
      return "#9e9e9e"; // אפור
  }
};